export interface SynergistRuntimeConfig {
    entraWorkSchoolSignInEnabled?: boolean;
    finalizedLifecycleReplayOrganizationIds?: readonly string[];
}

declare global {
    interface Window {
        __SYNERGIST_RUNTIME_CONFIG__?: SynergistRuntimeConfig;
    }
}

const EMPTY_ORGANIZATION_IDS: readonly string[] = Object.freeze([]);

const defaultRuntimeConfig: Readonly<Required<SynergistRuntimeConfig>> = Object.freeze({
    entraWorkSchoolSignInEnabled: false,
    finalizedLifecycleReplayOrganizationIds: EMPTY_ORGANIZATION_IDS,
});

const normalizeOrganizationIds = (configuredIds: unknown): readonly string[] => {
    if (!Array.isArray(configuredIds) || configuredIds.length === 0)
        return EMPTY_ORGANIZATION_IDS;

    const normalizedIds = configuredIds
        .filter((value): value is string => typeof value === 'string' && value.trim() !== '')
        .map(value => value.trim().toLowerCase());
    if (normalizedIds.length !== configuredIds.length || new Set(normalizedIds).size !== normalizedIds.length)
        return EMPTY_ORGANIZATION_IDS;

    return Object.freeze(normalizedIds);
};

export const getRuntimeConfig = (): Readonly<Required<SynergistRuntimeConfig>> => {
    const configured = typeof window === 'undefined' ? undefined : window.__SYNERGIST_RUNTIME_CONFIG__;
    return Object.freeze({
        ...defaultRuntimeConfig,
        ...configured,
        finalizedLifecycleReplayOrganizationIds: normalizeOrganizationIds(
            configured?.finalizedLifecycleReplayOrganizationIds,
        ),
    });
};

export const isEntraWorkSchoolSignInEnabled = (): boolean =>
    getRuntimeConfig().entraWorkSchoolSignInEnabled === true;

export const FINALIZED_LIFECYCLE_REPLAY_ORGANIZATION_IDS =
    getRuntimeConfig().finalizedLifecycleReplayOrganizationIds;

export const isFinalizedLifecycleReplayOrganization = (
    selectedOrganizationId: string,
    selectedOrganizationName: string,
    allowedOrganizationIds: readonly string[] = FINALIZED_LIFECYCLE_REPLAY_ORGANIZATION_IDS,
): boolean => selectedOrganizationName === 'Northstar Field Services Training'
    && selectedOrganizationId !== ''
    && allowedOrganizationIds.includes(selectedOrganizationId.toLowerCase());
